import type {
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  ListProjectsResponse,
  GetProjectResponse,
  SetProjectRequest,
  CreateProjectResponse,
  GetNodeTypesResponse,
  GetApplicationsResponse,
  GetStatusResponse,
  CostOverview,
  Project,
  DeploymentStatus,
  NodeType,
  Application,
} from './types';

// In development, use localhost backend. In production, use the same origin as the frontend
// Detect if running on localhost (dev) or production by checking hostname
const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3000'
  : '';
const TOKEN_KEY = 'token';

// Token management
let currentToken: string | null = localStorage.getItem(TOKEN_KEY);

export const getToken = (): string | null => currentToken;

export const setToken = (token: string): void => {
  currentToken = token;
  localStorage.setItem(TOKEN_KEY, token);
};

export const clearToken = (): void => {
  currentToken = null;
  localStorage.removeItem(TOKEN_KEY);
};

// Event listeners for auth changes
type AuthChangeListener = (token: string | null) => void;
const authChangeListeners: AuthChangeListener[] = [];

export const onAuthChange = (listener: AuthChangeListener): (() => void) => {
  authChangeListeners.push(listener);
  return () => {
    const index = authChangeListeners.indexOf(listener);
    if (index > -1) {
      authChangeListeners.splice(index, 1);
    }
  };
};

const notifyAuthChange = (token: string | null): void => {
  authChangeListeners.forEach(listener => listener(token));
};

// HTTP client
class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const request = async <T>(
  path: string,
  options: RequestInit = {}
): Promise<T> => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...options.headers,
  };

  if (currentToken) {
    headers.Authorization = `Bearer ${currentToken}`;
  }

  const url = `${API_BASE_URL}${path}`;
  console.log(`${options.method || 'GET'} ${url}`);

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle unauthorized
    if (response.status === 401) {
      console.log('Unauthorized, logging out');
      clearToken();
      notifyAuthChange(null);

      // Try to parse error message
      let errorMessage = 'Unauthorized';
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // Ignore parsing errors
      }

      throw new ApiError(errorMessage, 401, response.statusText);
    }

    // Handle other errors
    if (!response.ok) {
      // Try to parse error message from response
      let errorMessage = `Request failed: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // If parsing fails, use default message
      }

      throw new ApiError(
        errorMessage,
        response.status,
        response.statusText
      );
    }

    // Parse response
    if (response.status === 204) {
      return undefined as T;
    }

    // Check if response has content before parsing JSON
    const contentType = response.headers.get('content-type');
    const contentLength = response.headers.get('content-length');

    // Empty response body
    if (contentLength === '0' || !contentType?.includes('application/json')) {
      return undefined as T;
    }

    const data = await response.json();
    return data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      0,
      'Network Error'
    );
  }
};

// API methods
export const api = {
  // Authentication
  login: async (email: string, password: string): Promise<LoginResponse> => {
    try {
      const response = await request<{ token?: string; error?: string }>('/login', {
        method: 'POST',
        body: JSON.stringify({ email, password } as LoginRequest),
      });

      // Handle simple token response from backend
      if (response.token) {
        setToken(response.token);
        notifyAuthChange(response.token);
        return { type: 'success', token: response.token };
      }

      // Handle error response
      return {
        type: 'failure',
        error: response.error || 'Login failed',
      };
    } catch (error) {
      return {
        type: 'failure',
        error: error instanceof ApiError ? error.message : (error instanceof Error ? error.message : 'Login failed'),
      };
    }
  },

  refreshToken: async (): Promise<RefreshTokenResponse | null> => {
    try {
      const response = await request<RefreshTokenResponse>('/refresh-token', {
        method: 'POST',
      });

      if (response && response.token) {
        setToken(response.token);
        notifyAuthChange(response.token);
      }

      return response;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return null;
    }
  },

  logout: (): void => {
    clearToken();
    notifyAuthChange(null);
  },

  // Projects
  listProjects: async (): Promise<Project[]> => {
    try {
      const response = await request<ListProjectsResponse>('/projects');
      return response.projects;
    } catch (error) {
      console.error('Failed to list projects:', error);
      return [];
    }
  },

  getProject: async (id: string): Promise<Project | null> => {
    try {
      const response = await request<GetProjectResponse>(`/projects/${id}`);
      return response.project;
    } catch (error) {
      console.error(`Failed to get project ${id}:`, error);
      return null;
    }
  },

  createProject: async (
    project: Project,
    controlNodes: Record<string, number>,
    workerNodes: Record<string, number>
  ): Promise<CreateProjectResponse | null> => {
    try {
      return await request<CreateProjectResponse>('/projects/create', {
        method: 'POST',
        body: JSON.stringify({ project, controlNodes, workerNodes } as SetProjectRequest),
      });
    } catch (error) {
      console.error('Failed to create project:', error);
      return null;
    }
  },

  updateProject: async (
    id: string,
    project: Project,
    controlNodes: Record<string, number>,
    workerNodes: Record<string, number>,
    nodesToDelete: string[]
  ): Promise<void> => {
    try {
      await request<void>(`/projects/${id}`, {
        method: 'POST',
        body: JSON.stringify({ project, controlNodes, workerNodes, nodesToDelete } as SetProjectRequest),
      });
    } catch (error) {
      console.error(`Failed to update project ${id}:`, error);
      throw error;
    }
  },

  deleteProject: async (id: string): Promise<void> => {
    try {
      await request<void>(`/projects/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error(`Failed to delete project ${id}:`, error);
      throw error;
    }
  },

  // Deployment status
  getStatus: async (id: string): Promise<GetStatusResponse | null> => {
    try {
      const response = await request<GetStatusResponse>(`/projects/${id}/status`);
      return response;
    } catch (error) {
      console.error(`Failed to get status for project ${id}:`, error);
      return null;
    }
  },

  // Products
  getNodeTypes: async (): Promise<NodeType[]> => {
    try {
      const response = await request<GetNodeTypesResponse>('/products/nodetypes');
      return response.nodeTypes;
    } catch (error) {
      console.error('Failed to get node types:', error);
      return [];
    }
  },

  getApplications: async (): Promise<Application[]> => {
    try {
      const response = await request<GetApplicationsResponse>('/products/applications');
      return response.applications;
    } catch (error) {
      console.error('Failed to get applications:', error);
      return [];
    }
  },

  // Costs
  getCosts: async (): Promise<CostOverview | null> => {
    try {
      return await request<CostOverview>('/users/me/cost');
    } catch (error) {
      console.error('Failed to get costs:', error);
      return null;
    }
  },
};
